import { Fragment, useMemo, useState } from "react";
import {
  ScrollText,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Filter,
  User as UserIcon,
} from "lucide-react";
import styles from "./AuditLogs.module.css";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import { useToast } from "../../../context/ToastContext";
import {
  useAuditLogs,
  useAuditActions,
  useAuditEntities,
} from "../../../api/users/useAuditApi";

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
    : "—";

const actionTone = (action = "") => {
  const a = action.toUpperCase();
  if (a.includes("DELETE") || a.includes("REMOVE")) return styles.tDelete;
  if (a.includes("CREATE") || a.includes("ASSIGN")) return styles.tCreate;
  if (a.includes("UPDATE") || a.includes("CHANGE")) return styles.tUpdate;
  return styles.tNeutral;
};

const tryParse = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch {
    return v;
  }
};

const AuditLogs = () => {
  const { showToast } = useToast();

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expanded, setExpanded] = useState(null);

  const { data: logs, pagination, loading, error, refetch } = useAuditLogs({
    page,
    limit,
    search,
    action,
    entity,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo ? `${dateTo}T23:59:59` : undefined,
  });
  const { data: actions } = useAuditActions();
  const { data: entities } = useAuditEntities();

  const actionOptions = useMemo(
    () => [
      { label: "All Actions", value: "" },
      ...(actions || []).filter(Boolean).map((a) => ({ label: a, value: a })),
    ],
    [actions]
  );
  const entityOptions = useMemo(
    () => [
      { label: "All Entities", value: "" },
      ...(entities || []).filter(Boolean).map((e) => ({ label: e, value: e })),
    ],
    [entities]
  );

  const totalPages = pagination?.totalPages ?? 1;

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleRefresh = async () => {
    const res = await refetch();
    if (res?.success === false) showToast(res.message, "error");
    else showToast("Audit log refreshed", "success");
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setAction("");
    setEntity("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <ScrollText size={22} className={styles.titleIcon} />
            Audit &amp; Activity Logs
          </h1>
          <p className={styles.subtitle}>
            Immutable trail of every user, role and permission change
          </p>
        </div>
        <button className={styles.refreshBtn} onClick={handleRefresh}>
          <RefreshCw size={16} />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <Search size={16} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search action, entity or reason…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className={styles.searchBtn}>
            Search
          </button>
        </form>
        <SelectBox
          placeholder="All Actions"
          value={action}
          width="190px"
          options={actionOptions}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(1);
          }}
        />
        <SelectBox
          placeholder="All Entities"
          value={entity}
          width="180px"
          options={entityOptions}
          onChange={(e) => {
            setEntity(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className={styles.dateBar}>
        <div className={styles.dateGroup}>
          <Filter size={14} />
          <label>From</label>
          <input
            type="date"
            className={styles.dateInput}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
          />
          <label>To</label>
          <input
            type="date"
            className={styles.dateInput}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <button className={styles.resetBtn} onClick={resetFilters}>
          Reset filters
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.expandCol}></th>
              <th>Timestamp</th>
              <th>User</th>
              <th>Action</th>
              <th>Entity</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className={styles.center}>
                  Loading audit logs…
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.center}>
                  No audit entries found.
                </td>
              </tr>
            ) : (
              logs.map((log) => {
                const isOpen = expanded === log.id;
                const oldVal = tryParse(log.oldValue);
                const newVal = tryParse(log.newValue);
                const meta = tryParse(log.metadata);
                return (
                  <Fragment key={log.id}>
                    <tr
                      className={styles.row}
                      onClick={() => setExpanded(isOpen ? null : log.id)}
                    >
                      <td className={styles.expandCol}>
                        {isOpen ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </td>
                      <td className={styles.muted}>{fmtDateTime(log.createdAt)}</td>
                      <td>
                        <div className={styles.userCell}>
                          <span className={styles.userAvatar}>
                            <UserIcon size={13} />
                          </span>
                          <span>
                            {log.user?.name || `User #${log.userId ?? "—"}`}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`${styles.actionBadge} ${actionTone(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className={styles.entityCell}>
                        {log.entity}
                        {log.entityId ? (
                          <span className={styles.entityId}>#{log.entityId}</span>
                        ) : null}
                      </td>
                      <td className={styles.muted}>{log.reason || "—"}</td>
                    </tr>
                    {isOpen && (
                      <tr className={styles.detailRow}>
                        <td colSpan={6}>
                          <div className={styles.detailGrid}>
                            <div className={styles.detailCol}>
                              <span className={styles.detailLabel}>Before</span>
                              <pre className={styles.json}>
                                {oldVal
                                  ? JSON.stringify(oldVal, null, 2)
                                  : "—"}
                              </pre>
                            </div>
                            <div className={styles.detailCol}>
                              <span className={styles.detailLabel}>After</span>
                              <pre className={styles.json}>
                                {newVal
                                  ? JSON.stringify(newVal, null, 2)
                                  : "—"}
                              </pre>
                            </div>
                          </div>
                          <div className={styles.detailFooter}>
                            <span>
                              <b>IP:</b> {log.ipAddress || "—"}
                            </span>
                            <span className={styles.uaText}>
                              <b>Agent:</b> {log.userAgent || "—"}
                            </span>
                            {meta && typeof meta === "object" && (
                              <span>
                                <b>Meta:</b> {JSON.stringify(meta)}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>

        <div className={styles.footer}>
          <span className={styles.footerInfo}>
            {pagination?.total ?? 0} entries · page {pagination?.page ?? page} of{" "}
            {totalPages}
          </span>
          <div className={styles.pageNav}>
            <button
              className={styles.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ‹ Prev
            </button>
            <button
              className={styles.pageBtn}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
